import { useState } from "react";
import axios from "axios";
import { KeyRound, Users, RefreshCw } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Visitors = () => {
  const [code, setCode] = useState("");
  const [visitors, setVisitors] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const load = async (e) => {
    if (e) e.preventDefault();
    if (!code.trim()) {
      setError("Please enter the access code.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.get(`${API}/access/visitors`, {
        params: { code: code.trim() },
      });
      setVisitors(data);
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not load visitors.");
      setVisitors(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <main data-testid="visitors-page" className="pt-[130px] min-h-screen bg-[#FDFBF7]">
      <div className="max-w-3xl mx-auto px-5 pb-20">
        <p className="text-xs font-semibold uppercase tracking-widest text-rose mb-2">Owner only</p>
        <h1 className="font-display text-4xl font-medium tracking-tight text-ink">
          Who's visited
        </h1>
        <p className="text-sm text-gray-500 mt-3">
          Every entry through the access gate is logged below — newest first.
        </p>

        <form onSubmit={load} data-testid="visitors-code-form" className="mt-8 flex gap-3">
          <div className="relative flex-1">
            <KeyRound size={17} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              data-testid="visitors-code-input"
              placeholder="Access code"
              autoComplete="off"
              className="w-full rounded-md border border-gray-300 bg-white pl-11 pr-4 py-3 text-ink placeholder:text-gray-400 outline-none focus:border-ink focus:ring-2 focus:ring-ink/10 transition-colors duration-200"
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            data-testid="visitors-load-btn"
            className="flex items-center gap-2 rounded-md bg-ink text-white font-semibold px-6 py-3 hover:bg-rose transition-colors duration-200 disabled:opacity-50"
          >
            {loading ? "Loading…" : "View log"}
          </button>
        </form>
        {error && (
          <p data-testid="visitors-error" className="text-sm text-red-600 mt-3">
            {error}
          </p>
        )}

        {visitors && (
          <div data-testid="visitors-list" className="mt-8">
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm text-gray-500 flex items-center gap-2">
                <Users size={15} /> {visitors.length} {visitors.length === 1 ? "entry" : "entries"}
              </p>
              <button
                type="button"
                onClick={() => load()}
                data-testid="visitors-refresh-btn"
                className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-ink transition-colors duration-200"
              >
                <RefreshCw size={14} /> Refresh
              </button>
            </div>
            <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
              {visitors.length === 0 ? (
                <p data-testid="visitors-empty" className="text-sm text-gray-500 text-center py-12">
                  No entries yet.
                </p>
              ) : (
                <table className="w-full text-left">
                  <thead>
                    <tr className="border-b border-gray-200 bg-gray-50/60">
                      <th className="text-xs font-semibold uppercase tracking-widest text-gray-500 px-6 py-3">Name</th>
                      <th className="text-xs font-semibold uppercase tracking-widest text-gray-500 px-6 py-3">Time</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visitors.map((v) => (
                      <tr key={v.id} data-testid={`visitor-row-${v.id}`} className="border-b border-gray-100 last:border-0">
                        <td className="px-6 py-4 text-sm font-medium text-ink">{v.name}</td>
                        <td className="px-6 py-4 text-sm text-gray-500">
                          {new Date(v.created_at).toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}
      </div>
    </main>
  );
};

export default Visitors;
